import type {
  FieldWorkRequest,
  FieldWorkRequestCreateInput,
  FieldWorkRequestPatch,
} from '../domain/fieldWorkRequest';
import type { FieldOpsTeam } from '../domain/fieldOpsTeam';
import {
  computeFieldWorkStats,
  fieldWorkStatsFromWire,
  type FieldWorkStats,
} from '../domain/fieldWorkReporting';
import { fieldWorkRequestSchema, fieldWorkStatsWireSchema } from '../domain/validation';
import {
  ApiError,
  ConflictError,
  DuplicateError,
  ForbiddenError,
  NotFoundError,
  ValidationError,
} from './errors';
import { withoutUndefined } from './patch';
import type { FieldWorkApi, FieldWorkListFilters } from './field-work-api';

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api';

function redirectToSignIn(): void {
  const returnTo = `${window.location.pathname}${window.location.search}`;
  window.location.assign(`/auth/google?returnTo=${encodeURIComponent(returnTo)}`);
}

/**
 * Talks to the local dev API server (see server/), which relays every
 * /v1/* request to the real leatherback backend. Model and error-mapping
 * mirror live-shipments-api.ts.
 */
export class LiveFieldWorkApi implements FieldWorkApi {
  private async fetchJSON(path: string, init?: RequestInit): Promise<Record<string, unknown>> {
    let response: Response;
    try {
      response = await fetch(`${BASE_URL}${path}`, {
        ...init,
        headers: { 'content-type': 'application/json', ...(init?.headers ?? {}) },
      });
    } catch (error) {
      throw new ApiError(
        error instanceof Error ? error.message : 'Network request failed',
        'NETWORK',
      );
    }
    const body = (await response.json().catch(() => ({}))) as Record<string, unknown>;
    if (!response.ok) {
      if (response.status === 401) {
        redirectToSignIn();
        throw new ApiError('Your session expired. Redirecting to sign-in…', 'UNAUTHENTICATED');
      }
      const message =
        typeof body.error === 'string'
          ? body.error
          : typeof body.message === 'string'
            ? body.message
            : `Request failed with status ${response.status}`;
      const currentVersion =
        typeof body.currentVersion === 'number' ? body.currentVersion : undefined;
      const code = body.code === 5 ? 'NOT_FOUND' : body.code;
      switch (code) {
        case 'NOT_FOUND':
          throw new NotFoundError(message);
        case 'CONFLICT':
          throw new ConflictError(message, currentVersion);
        case 'DUPLICATE':
          throw new DuplicateError(message);
        case 'VALIDATION':
          throw new ValidationError(message);
        default:
          if (/version .* does not match/i.test(message)) throw new ConflictError(message);
          if (/not authorized/i.test(message)) throw new ForbiddenError(message);
          throw new ApiError(message, 'INTERNAL');
      }
    }
    return body;
  }

  async listFieldWork(filters?: FieldWorkListFilters): Promise<FieldWorkRequest[]> {
    const params = new URLSearchParams({ pageSize: '5000' });
    if (filters?.team) params.set('team', filters.team);
    if (filters?.status) params.set('status', filters.status);
    if (filters?.assignee) params.set('assignee', filters.assignee);
    const raw = await this.fetchJSON(`/v1/fieldWorkRequests?${params.toString()}`);
    const rows = Array.isArray(raw.fieldWorkRequests) ? raw.fieldWorkRequests : [];
    return rows.map((row) => fieldWorkRequestSchema.parse(row));
  }

  async getFieldWork(id: string): Promise<FieldWorkRequest> {
    const raw = await this.fetchJSON(`/v1/fieldWorkRequests/${encodeURIComponent(id)}`);
    return fieldWorkRequestSchema.parse(raw);
  }

  async createFieldWork(input: FieldWorkRequestCreateInput): Promise<FieldWorkRequest> {
    const raw = await this.fetchJSON('/v1/fieldWorkRequests', {
      method: 'POST',
      body: JSON.stringify(withoutUndefined(input)),
    });
    return fieldWorkRequestSchema.parse(raw);
  }

  async updateFieldWork(patch: FieldWorkRequestPatch): Promise<FieldWorkRequest> {
    const { id, ...rest } = patch;
    const raw = await this.fetchJSON(`/v1/fieldWorkRequests/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(withoutUndefined(rest)),
    });
    return fieldWorkRequestSchema.parse(raw);
  }

  async addNote(id: string, note: string): Promise<FieldWorkRequest> {
    const raw = await this.fetchJSON(`/v1/fieldWorkRequests/${encodeURIComponent(id)}:addNote`, {
      method: 'POST',
      body: JSON.stringify({ note: note.trim() }),
    });
    return fieldWorkRequestSchema.parse(raw);
  }

  async handOff(
    id: string,
    version: number,
    toAssignee: string,
    note: string,
  ): Promise<FieldWorkRequest> {
    const raw = await this.fetchJSON(`/v1/fieldWorkRequests/${encodeURIComponent(id)}:handOff`, {
      method: 'POST',
      body: JSON.stringify({ version: String(version), toAssignee, note: note.trim() }),
    });
    return fieldWorkRequestSchema.parse(raw);
  }

  async reorderFieldWorkQueue(
    team: FieldOpsTeam,
    ordered: { id: string; version: number }[],
  ): Promise<FieldWorkRequest[]> {
    const raw = await this.fetchJSON('/v1/fieldWorkRequests:reorder', {
      method: 'POST',
      body: JSON.stringify({
        team,
        orderedIds: ordered.map((entry) => entry.id),
        versions: ordered.map((entry) => entry.version),
      }),
    });
    const rows = Array.isArray(raw.fieldWorkRequests) ? raw.fieldWorkRequests : [];
    return rows.map((row) => fieldWorkRequestSchema.parse(row));
  }

  async getFieldWorkStats(team?: FieldOpsTeam): Promise<FieldWorkStats> {
    try {
      const params = new URLSearchParams();
      if (team) params.set('team', team);
      const raw = await this.fetchJSON(`/v1/fieldWorkRequests:stats?${params.toString()}`);
      return fieldWorkStatsFromWire(fieldWorkStatsWireSchema.parse(raw));
    } catch {
      // Backend without the RPC (or a transient failure) -- compute from
      // the list instead, same numbers.
      const requests = await this.listFieldWork(team ? { team } : undefined);
      return computeFieldWorkStats(requests);
    }
  }
}

export const fieldWorkApi: FieldWorkApi = new LiveFieldWorkApi();
